import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import { useEffect, useState } from "react";
import { authMe, getApiKey, hasCapability, setApiKey, signOut, type ConsoleAuthState } from "./lib/api";
import { SystemProvider, useSystem } from "./lib/system";
import { Dot } from "./components/ui";
import OverviewPage from "./pages/Overview";
import LoginPage from "./pages/Login";
import UsersPage from "./pages/Users";
import ProvidersPage from "./pages/Providers";
import ModelsPage from "./pages/Models";
import RouterPage from "./pages/Router";
import RequestsPage from "./pages/Requests";
import AnalyticsPage from "./pages/Analytics";
import KeysPage from "./pages/Keys";
import PlaygroundPage from "./pages/Playground";
import SettingsPage from "./pages/Settings";

const NAV_GROUPS: Array<{
  label: string | null;
  items: Array<{
    to: string;
    label: string;
    end?: boolean;
    /** Hidden unless the signed-in operator has this capability. */
    capability?: "read" | "write" | "manage_credentials" | "manage_users";
  }>;
}> = [
  {
    label: "Get started",
    items: [
      { to: "/keys", label: "Keys" },
      { to: "/", label: "Overview", end: true },
    ],
  },
  {
    label: "Inference",
    items: [
      { to: "/providers", label: "Providers" },
      { to: "/models", label: "Models" },
      { to: "/router", label: "Routing" },
    ],
  },
  {
    label: "Observability",
    items: [
      { to: "/requests", label: "Requests" },
      { to: "/analytics", label: "Analytics" },
    ],
  },
  {
    label: "Developer",
    items: [{ to: "/playground", label: "Playground" }],
  },
  {
    label: "System",
    items: [
      { to: "/users", label: "Operators", capability: "manage_users" },
      { to: "/settings", label: "Settings" },
    ],
  },
];

function SidebarStatus({ auth }: { auth: ConsoleAuthState | null }) {
  const { overview } = useSystem();
  if (!overview) return null;
  // Production-facing counts exclude demo/mock providers.
  const providers = (overview.providers ?? []).filter(
    (p) => !p.providerTypeId.startsWith("mock-"),
  );
  const healthy = providers.filter((p) => p.health === "healthy" || p.status === "active").length;
  const degraded = providers.some((p) => p.health === "unhealthy" || p.health === "degraded");
  const freeSources = providers.filter((p) => (p.freeModelCount ?? 0) > 0).length;
  const freeModels = providers.reduce((s, p) => s + (p.freeModelCount ?? 0), 0);

  return (
    <div className="border-t border-canvas-border p-3">
      <div className="rounded-lg border border-canvas-border bg-canvas px-3 py-2.5">
        <div className="flex items-center gap-2 text-xs font-medium">
          <Dot tone={degraded ? "warn" : "ok"} pulse={!degraded} />
          {degraded ? "NEXUS degraded" : "NEXUS operational"}
        </div>
        <div className="mt-1.5 text-[11px] text-canvas-muted">
          Free pool
          <span className="ml-1.5 font-medium text-canvas-ink">
            {freeModels} models · {freeSources} sources
          </span>
        </div>
        <div className="text-[11px] text-canvas-muted">
          {healthy}/{providers.length} providers healthy
        </div>
      </div>
      {auth?.user ? (
        <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-canvas-border bg-canvas px-3 py-2">
          <div className="min-w-0">
            <div className="truncate text-xs font-medium">{auth.user.username}</div>
            <div className="text-[10px] uppercase tracking-wider text-canvas-muted">
              {auth.user.role}
            </div>
          </div>
        </div>
      ) : null}
      <button
        className="nav-link mt-1 w-full text-left !text-xs"
        onClick={() => {
          void signOut().finally(() => window.location.reload());
        }}
      >
        Sign out
      </button>
    </div>
  );
}

export default function App() {
  const [key, setKeyState] = useState(getApiKey());
  const [draft, setDraft] = useState(key);
  const [hydrated, setHydrated] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [auth, setAuth] = useState<ConsoleAuthState | null>(null);
  const [useApiKey, setUseApiKey] = useState(false);

  useEffect(() => {
    setHydrated(true);
    let cancelled = false;
    authMe()
      .then((state) => {
        if (!cancelled) setAuth(state);
      })
      .catch(() => {
        // Instances without console credentials fall through to API-key sign-in.
        if (!cancelled) {
          setAuth({
            authenticated: false,
            signInEnabled: false,
            user: null,
            environment: "unknown",
            sessionTtlHours: 0,
            minPasswordLength: 10,
            roles: [],
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function saveKey() {
    setApiKey(draft.trim());
    setKeyState(draft.trim());
    setUseApiKey(true);
  }

  if (!hydrated || !auth) return null;

  // Console sign-in takes precedence; the API-key screen stays available for
  // programmatic/operator access on instances that have no console password.
  if (!auth.authenticated && auth.signInEnabled && !useApiKey) {
    return (
      <LoginPage
        onSignedIn={() => {
          authMe().then(setAuth).catch(() => setAuth(null));
        }}
        onUseApiKey={() => setUseApiKey(true)}
      />
    );
  }

  if (!key && !auth.authenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="panel w-full max-w-lg p-8">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">
            InferMesh
          </div>
          <h1 className="mb-2 text-2xl font-semibold tracking-tight">NEXUS Console</h1>
          <p className="mb-6 text-sm text-canvas-muted">
            Free-first intelligent AI gateway. Connect with a local development API key
            (printed by <span className="mono text-accent">npm run seed</span> or the server logs).
          </p>
          <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-canvas-muted">
            API Key
          </label>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && saveKey()}
            placeholder="sk-im-…"
            className="mb-4 w-full rounded-lg border border-canvas-border bg-canvas px-3 py-2 font-mono text-sm outline-none focus:border-accent"
            autoFocus
          />
          <button className="btn-primary w-full justify-center" onClick={saveKey}>
            Open Console
          </button>
          <p className="mt-4 text-xs text-canvas-muted">
            Gateway: <span className="mono">{window.location.origin}</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <SystemProvider>
      <div className="flex min-h-screen">
        {/* Mobile top bar */}
        <div className="fixed inset-x-0 top-0 z-30 flex items-center justify-between border-b border-canvas-border bg-canvas-raised px-4 py-3 md:hidden">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-accent">
              InferMesh
            </div>
            <div className="text-sm font-semibold">NEXUS Console</div>
          </div>
          <button
            className="btn-ghost !px-2.5 !py-1.5"
            aria-label="Menu"
            aria-expanded={navOpen}
            onClick={() => setNavOpen((v) => !v)}
          >
            ☰
          </button>
        </div>

        {/* Sidebar: overlay on mobile, fixed on desktop */}
        {navOpen && (
          <button
            className="fixed inset-0 z-30 bg-black/60 md:hidden"
            aria-label="Close menu"
            onClick={() => setNavOpen(false)}
          />
        )}
        <aside
          className={`fixed inset-y-0 left-0 z-40 flex w-56 flex-col border-r border-canvas-border bg-canvas-raised transition-transform md:translate-x-0 ${
            navOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="border-b border-canvas-border px-4 py-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-accent">
              InferMesh
            </div>
            <div className="text-sm font-semibold">NEXUS Console</div>
          </div>
          <nav className="flex-1 space-y-4 overflow-y-auto p-3">
            {NAV_GROUPS.map((group, gi) => {
              const items = group.items.filter(
                (item) => !item.capability || hasCapability(auth, item.capability),
              );
              if (items.length === 0) return null;
              return (
                <div key={gi}>
                  {group.label && (
                    <div className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-canvas-muted/70">
                      {group.label}
                    </div>
                  )}
                  <div className="space-y-0.5">
                    {items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        onClick={() => setNavOpen(false)}
                        className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                      >
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              );
            })}
          </nav>
          <SidebarStatus auth={auth} />
        </aside>

        <main className="min-w-0 flex-1 p-4 pt-20 md:ml-56 md:p-6">
          <Routes>
            <Route path="/access" element={<Navigate to="/keys" replace />} />
            <Route path="/" element={<OverviewPage />} />
            <Route path="/providers" element={<ProvidersPage />} />
            <Route path="/models" element={<ModelsPage />} />
            <Route path="/router" element={<RouterPage />} />
            <Route path="/requests" element={<RequestsPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/keys" element={<KeysPage />} />
            <Route path="/playground" element={<PlaygroundPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </SystemProvider>
  );
}
